import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ExternalLink, Plus, BookOpen, Loader2, ChevronDown, ChevronUp, FolderPlus } from "lucide-react";
import { useApp } from "@/app/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";

export default function ScienceSearch() {
  const { setCredits, refreshMeta } = useApp();
  const [q, setQ] = useState("");
  const [source, setSource] = useState("both");
  const [yearFrom, setYearFrom] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("none");

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const params = { q, source };
      if (yearFrom) params.year_from = yearFrom;
      const { data } = await api.get(`/science/search`, { params });
      setResults(data.results); setCredits(data.credits); refreshMeta();
      if (data.results.length === 0) toast.info("Aucun résultat trouvé.");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const addBiblio = async (r) => {
    try {
      await api.post("/bibliography", { title: r.title, authors: (r.authors || []).join(", "), year: String(r.year || ""), venue: r.venue || "", doi: r.doi || "", source: r.source, project_id: projectId !== "none" ? projectId : null });
      toast.success("Ajouté à la bibliographie");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const addToReview = async (r) => {
    if (projectId === "none") { toast.error("Sélectionnez d'abord un projet."); return; }
    try {
      const { data: p } = await api.get(`/projects/${projectId}`);
      const cite = `• ${(r.authors || []).join(", ")} (${r.year || "s.d."}). ${r.title}. ${r.venue || ""}${r.doi ? " — DOI: " + r.doi : ""}`;
      const lit = (p.literature_review ? p.literature_review + "\n" : "") + cite;
      await api.put(`/projects/${projectId}`, { ...p, literature_review: lit });
      toast.success("Référence ajoutée à la revue de littérature du projet");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Revue de littérature</h1>
      <p className="text-muted2 mt-1">Explorez la littérature scientifique en SIC via <strong>OpenAlex</strong> et <strong>Crossref</strong>. Publications réelles, DOI vérifiables — aucune référence inventée.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Concept, théorie, objet d'étude…" className="pl-10 h-11" data-testid="science-query" />
        </div>
        <Input value={yearFrom} onChange={(e) => setYearFrom(e.target.value.replace(/\D/g, ""))} placeholder="Depuis (année)" className="h-11 w-36" data-testid="science-year" />
        <Button onClick={search} disabled={loading} className="bg-navy text-white hover:bg-navy-800 h-11 px-6 rounded-lg" data-testid="science-search-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rechercher"}</Button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Tabs value={source} onValueChange={setSource}>
          <TabsList>
            <TabsTrigger value="both" data-testid="source-both">Les deux</TabsTrigger>
            <TabsTrigger value="openalex" data-testid="source-openalex">OpenAlex</TabsTrigger>
            <TabsTrigger value="crossref" data-testid="source-crossref">Crossref</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="h-9 w-56" data-testid="review-project-select"><SelectValue placeholder="Associer à un projet" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Aucun projet</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 space-y-3">
        {results.map((r, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#E5E7EB] p-5 hover:shadow-sm transition-all" data-testid={`science-result-${i}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="font-semibold text-navy leading-snug">{r.title}</h3>
                <p className="text-sm text-muted2 mt-1">{(r.authors || []).join(", ")}{r.year ? ` · ${r.year}` : ""}</p>
                {r.venue && <p className="text-xs text-muted2 italic mt-0.5">{r.venue}</p>}
                <div className="flex flex-wrap items-center gap-2 mt-2 text-xs font-mono text-muted2">
                  <span className="bg-navy/5 px-2 py-0.5 rounded">{r.source}</span>
                  {r.open_access && <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded">Accès libre</span>}
                  {r.doi && <span>DOI: {r.doi}</span>}
                  {r.citations != null && <span>{r.citations} citations</span>}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0 w-40">
                <Button size="sm" onClick={() => addBiblio(r)} className="bg-lisungi-yellow text-navy hover:bg-lisungi-yellow/90 rounded-full gap-1" data-testid={`add-biblio-${i}`}><Plus className="h-3.5 w-3.5" /> Bibliographie</Button>
                <Button size="sm" variant="outline" onClick={() => addToReview(r)} className="rounded-full gap-1 border-navy text-navy" data-testid={`add-review-${i}`}><FolderPlus className="h-3.5 w-3.5" /> Revue de litt.</Button>
                {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-navy hover:text-lisungi-yellow flex items-center gap-1 justify-center"><ExternalLink className="h-3 w-3" /> Ouvrir</a>}
              </div>
            </div>
            {r.abstract && (
              <div className="mt-3 pt-3 border-t border-[#F1F1F1]">
                <button onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))} className="text-xs font-medium text-navy flex items-center gap-1">
                  {expanded[i] ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} Résumé
                </button>
                {expanded[i] && <p className="text-sm text-ink mt-2 leading-relaxed">{r.abstract}</p>}
              </div>
            )}
          </div>
        ))}
        {results.length === 0 && !loading && (
          <div className="text-center py-16 text-muted2"><BookOpen className="h-8 w-8 mx-auto mb-3" />Lancez une recherche pour construire votre revue de littérature.</div>
        )}
      </div>
    </div>
  );
}
